import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { forgotPassword, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Eye, Loader2, Mail, ArrowLeft, KeyRound, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email, lang);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-10" data-testid="forgot-password-page">
      <button onClick={() => navigate("/login")} className="flex items-center gap-2.5 mb-8" data-testid="forgot-brand-home">
        <span className="w-10 h-10 rounded-md bg-primary text-white flex items-center justify-center">
          <Eye className="w-5 h-5" strokeWidth={2} />
        </span>
        <span className="leading-tight text-left">
          <span className="block text-lg font-bold tracking-tight">VisioScreening</span>
          <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
        </span>
      </button>

      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{t("forgot_title")}</h1>
        <p className="mt-1.5 text-sm text-slate-500">{t("forgot_sub")}</p>

        {!sent ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("forgot_email_label")}</Label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="forgot-email-input" type="email" required className="pl-9" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder={t("email_ph")} />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
              className="w-full h-11 text-base font-semibold bg-primary hover:bg-primary/90">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("wait")}</> : t("forgot_submit")}
            </Button>
          </form>
        ) : (
          <div className="mt-6 space-y-4" data-testid="forgot-result">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <MailCheck className="w-5 h-5 shrink-0 text-emerald-600" strokeWidth={1.8} />
              <div>
                <p className="text-sm text-emerald-900 font-medium" data-testid="forgot-sent-text">{t("forgot_generic")}</p>
                <p className="mt-1 text-xs text-emerald-700">{t("forgot_open_hint")}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">{t("forgot_spam_hint")}</p>
          </div>
        )}

        <button onClick={() => navigate("/login")} className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-login-btn">
          <ArrowLeft className="w-4 h-4" /> {t("back_to_login")}
        </button>
      </div>
    </div>
  );
}
